import {
  ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS,
  BOLT_PROJECT_PARAMETER_CATEGORY_OPTIONS,
  type AdditionalCategoriesConfiguration,
  type AdditionalCategoriesFormFieldKey,
  type AdditionalCategoriesProjectParameterCategory,
  type BoltConfiguration,
  type BoltConfigurationFormFieldKey,
  type BoltProjectParameterCategory,
  type ConnectorBindingConfiguration,
  type ConnectorBindingFormFieldKey,
  type ConnectorParameterBindingKind,
  type ConnectorParameterDestination,
  type MenuConfiguration,
  type Option,
  type ProcessPropertiesConfiguration,
  type Screen,
} from '../../types';
import {
  DEFAULT_PARAMETER_WORKFLOW_MODE,
  DEFAULT_PARAMETER_WORKFLOW_PARAMETERS,
  DEFAULT_PARAMETER_WORKFLOW_SELECTED_CATEGORIES,
  DEFAULT_PARAMETER_WORKFLOW_SHARED_PARAMETER_FILE_PATH,
  DEFAULT_PARAMETER_WORKFLOW_SYSTEM_GROUPING,
  PARAMETER_WORKFLOW_CATEGORY_OPTIONS,
} from '../../config/parameterWorkflowDefaults';
import type {
  ParameterOption,
  RevitCategory,
  WorkflowMode,
} from '../../stores/useParameterWorkflowStore';
import { normalizePipeexUiWizardProgressSnapshot } from './pipeexWizardProgressState';
import {
  buildPipeexUiWizardMappingSnapshot,
  normalizePipeexUiWizardMappingSnapshot,
} from './mappingProfileSnapshot';
import type { PipeexUiWizardMappingSnapshot, PipeexUiWizardMappingSource } from './mappingProfileSnapshot';
export { normalizePipeexUiWizardProgressSnapshot } from './pipeexWizardProgressState';
export type { PipeexUiWizardMappingSnapshot } from './mappingProfileSnapshot';
const KNOWN_WORKFLOW_OPTIONS = new Set<Exclude<Option, null>>([
  'Iso Drawings Generation',
  'Pressure Drop Calculations',
  'Stress Calculations',
]);

const normalizeWorkflowOption = (raw: unknown): Option => {
  if (typeof raw !== 'string') {
    return null;
  }

  const normalized = raw.trim();
  return KNOWN_WORKFLOW_OPTIONS.has(normalized as Exclude<Option, null>)
    ? (normalized as Exclude<Option, null>)
    : null;
};

export const PIPEEX_UI_PROCESS_FIELD_IDS = [
  'processConditionsValue',
  'projectPropertiesValue',
  'pipingSpecValuePC',
  'tracingSpecValuePC',
  'attribute12Value',
  'attribute13Value',
  'attribute14Value',
  'attribute15Value',
  'attribute16Value',
  'attribute17Value',
  'attribute18Value',
  'attribute19Value',
  'attribute20Value',
  'attribute21Value',
  'attribute22Value',
  'attribute23Value',
  'projectIdentifierValue',
  'projectNameValue',
  'areaValue',
  'attribute1ClientName',
  'attribute2ProjectAddress',
  'attribute3ClientDepartment',
  'attribute4ClientProjectNumber',
  'attribute5ClientDocumentNumber',
  'attribute6ClientCode',
  'attribute7EngPartyOffice',
  'attribute8EngPartyOfficeLocation',
  'attribute9EngPartyProjectNumber',
  'attribute10EngPartyAreaCode',
] as const satisfies readonly (keyof ProcessPropertiesConfiguration)[];

export type PipeexUiProcessFieldId = (typeof PIPEEX_UI_PROCESS_FIELD_IDS)[number];

export type PipeexUiProcessPropertiesSnapshot = Partial<
  Record<PipeexUiProcessFieldId, string>
>;

export const PIPEEX_UI_CONNECTOR_FIELD_IDS = [
  'endpoint1Value',
  'endpoint2Value',
  'endpoint3Value',
  'endpoint4Value',
  'materialValue',
  'designStdValue',
  'pressureClassValue',
  'insulationSpecValue',
  'insulationThicknessValue',
  'weightValue',
  'tracingSpecValue',
  'itemDescriptionValue',
] as const satisfies readonly ConnectorBindingFormFieldKey[];

export type PipeexUiConnectorFieldId = (typeof PIPEEX_UI_CONNECTOR_FIELD_IDS)[number];

export const PIPEEX_UI_BOLT_FIELD_IDS = [
  'boltModelingChoice',
  'boltBaseFamilyValue',
  'boltSetActiveValue',
  'boltItemDescriptionValue',
  'boltDiaValue',
  'boltQuantityValue',
  'boltLengthValue',
  'boltMaterialValue',
  'boltStandardValue',
] as const satisfies readonly BoltConfigurationFormFieldKey[];

export type PipeexUiBoltFieldId = (typeof PIPEEX_UI_BOLT_FIELD_IDS)[number];

export const PIPEEX_UI_ADDITIONAL_CATEGORIES_FIELD_IDS = [
  'valveTagValue',
  'supportsTagValue',
  'supportsSsrefValue',
  'instrumentTagValue',
] as const satisfies readonly AdditionalCategoriesFormFieldKey[];

export type PipeexUiAdditionalCategoriesFieldId =
  (typeof PIPEEX_UI_ADDITIONAL_CATEGORIES_FIELD_IDS)[number];

export interface PipeexUiWizardSetupSnapshot {
  workflowMode: WorkflowMode;
  useDefaultFile: boolean;
  sharedParameterFilePath: string;
  sharedParameterFileContent?: string;
  parameters: ParameterOption[];
  selectedCategories: RevitCategory[];
  systemGroupingPreference: string;
}

export interface PipeexUiWizardMenuSnapshot {
  selectedOption: Option;
  objectTypeValue: string;
  sKeyValue: string;
  pipingSpecValue: string;
  systemGroupingValue: string;
}

export interface PipeexUiWizardConnectorSnapshot {
  parameterDestination: ConnectorParameterDestination;
  parameterBindingKind: ConnectorParameterBindingKind;
  fields: Partial<Record<PipeexUiConnectorFieldId, string>>;
}

export interface PipeexUiWizardBoltSnapshot {
  parameterDestination: ConnectorParameterDestination;
  parameterBindingKind: ConnectorParameterBindingKind;
  projectParameterCategories: BoltProjectParameterCategory[];
  fields: Partial<Record<PipeexUiBoltFieldId, string>>;
}

export interface PipeexUiWizardAdditionalCategoriesSnapshot {
  parameterDestination: ConnectorParameterDestination;
  parameterBindingKind: ConnectorParameterBindingKind;
  projectParameterCategories: AdditionalCategoriesProjectParameterCategory[];
  fields: Partial<Record<PipeexUiAdditionalCategoriesFieldId, string>>;
}

export interface PipeexUiWizardProcessPropertiesSnapshot {
  processConditionsChecked: boolean;
  projectPropertiesChecked: boolean;
  // The "Include standard PipeEX parameters?" answer; `null` = never asked
  // (legacy profiles and fresh wizard runs).
  includeDefaultPipeexParameters?: boolean | null;
  fields: PipeexUiProcessPropertiesSnapshot;
}

export interface PipeexUiWizardProgressSectionsSnapshot {
  connectorBinding: 'connectors' | 'bom' | 'components';
  boltConnection: 'core' | 'supporting';
  processProperties: 'processConditions' | 'projectProperties';
  additionalCategories: 'valve' | 'supports' | 'instrument';
}

export interface PipeexUiWizardProgressSnapshot {
  screen: Screen;
  navigationHistory: Screen[];
  sections: PipeexUiWizardProgressSectionsSnapshot;
  hasSavedOnce: boolean;
  isWorkflowApplied: boolean;
}

export interface PipeexUiWizardSnapshot {
  setup: PipeexUiWizardSetupSnapshot;
  menu: PipeexUiWizardMenuSnapshot;
  connector: PipeexUiWizardConnectorSnapshot;
  bolt: PipeexUiWizardBoltSnapshot;
  additionalCategories: PipeexUiWizardAdditionalCategoriesSnapshot;
  processProperties: PipeexUiWizardProcessPropertiesSnapshot;
  mapping: PipeexUiWizardMappingSnapshot;
  progress?: PipeexUiWizardProgressSnapshot;
}

export interface PipeexUiWizardProfileSource {
  parameterWorkflow: Pick<
    PipeexUiWizardSetupSnapshot,
    | 'workflowMode'
    | 'useDefaultFile'
    | 'sharedParameterFilePath'
    | 'sharedParameterFileContent'
    | 'parameters'
    | 'selectedCategories'
    | 'systemGroupingPreference'
  >;
  menu: MenuConfiguration;
  connector: ConnectorBindingConfiguration;
  bolt: BoltConfiguration;
  additionalCategories: AdditionalCategoriesConfiguration;
  process: ProcessPropertiesConfiguration;
  mapping: PipeexUiWizardMappingSource;
  progress?: PipeexUiWizardProgressSnapshot | null;
}

const getTextValue = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const getFormFieldValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (value && typeof value === 'object' && 'value' in value) {
    const formFieldValue = (value as { value?: unknown }).value;
    return typeof formFieldValue === 'string'
      ? formFieldValue
      : String(formFieldValue ?? '');
  }

  return '';
};

const normalizeBoolean = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : fallback;

const normalizeOptionalBoolean = (value: unknown): boolean | null =>
  typeof value === 'boolean' ? value : null;

const normalizeWorkflowMode = (
  value: unknown,
  fallback: WorkflowMode = DEFAULT_PARAMETER_WORKFLOW_MODE
): WorkflowMode =>
  value === 'legacy' || value === 'multipage' ? value : fallback;

const normalizeParameterDestination = (
  value: unknown,
  fallback: ConnectorParameterDestination = 'project'
): ConnectorParameterDestination =>
  value === 'project' || value === 'family' ? value : fallback;

const normalizeParameterBindingKind = (
  value: unknown,
  fallback: ConnectorParameterBindingKind = 'type'
): ConnectorParameterBindingKind =>
  value === 'type' || value === 'instance' ? value : fallback;

const normalizeAdditionalCategoriesParameterBindingKind = (
  value: unknown
): ConnectorParameterBindingKind => normalizeParameterBindingKind(value, 'instance');

const normalizeParameterOptionsSnapshot = (raw: unknown): ParameterOption[] => {
  if (!Array.isArray(raw)) {
    return DEFAULT_PARAMETER_WORKFLOW_PARAMETERS.map((parameter) => ({ ...parameter }));
  }

  const normalized = raw
    .filter(
      (entry): entry is Partial<ParameterOption> =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as { name?: unknown }).name === 'string'
    )
    .map((entry) => {
      const parameter: ParameterOption = {
        name: getTextValue(entry.name).trim(),
        selected: entry.selected === true,
      };

      if (typeof entry.description === 'string') {
        parameter.description = entry.description;
      }

      if (typeof entry.dataType === 'string' && entry.dataType.trim().length > 0) {
        parameter.dataType = entry.dataType.trim() as ParameterOption['dataType'];
      }

      return parameter;
    })
    .filter((entry) => entry.name.length > 0);

  return normalized.length > 0
    ? normalized
    : DEFAULT_PARAMETER_WORKFLOW_PARAMETERS.map((parameter) => ({ ...parameter }));
};

const normalizeAllowedStringArray = <T extends string>(
  raw: unknown,
  allowedValues: readonly T[],
  fallback: readonly T[]
): T[] => {
  if (!Array.isArray(raw)) {
    return [...fallback];
  }

  const allowed = new Set<string>(allowedValues);
  return Array.from(
    new Set(
      raw.filter((value): value is T => typeof value === 'string' && allowed.has(value))
    )
  );
};

const buildFormFieldSnapshot = <TFieldId extends string>(
  fieldIds: readonly TFieldId[],
  source: Partial<Record<TFieldId, unknown>>
): Partial<Record<TFieldId, string>> =>
  fieldIds.reduce<Partial<Record<TFieldId, string>>>((snapshot, fieldId) => {
    snapshot[fieldId] = getFormFieldValue(source[fieldId]);
    return snapshot;
  }, {});

const normalizeFormFieldSnapshot = <TFieldId extends string>(
  raw: unknown,
  fieldIds: readonly TFieldId[]
): Partial<Record<TFieldId, string>> => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  const rawFields = raw as Partial<Record<TFieldId, unknown>>;
  return fieldIds.reduce<Partial<Record<TFieldId, string>>>((snapshot, fieldId) => {
    if (Object.prototype.hasOwnProperty.call(rawFields, fieldId)) {
      snapshot[fieldId] = getTextValue(rawFields[fieldId]);
    }
    return snapshot;
  }, {});
};

const normalizeProcessPropertiesSnapshot = (
  raw: Partial<Record<string, unknown>> | null | undefined
): PipeexUiProcessPropertiesSnapshot => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  return PIPEEX_UI_PROCESS_FIELD_IDS.reduce<PipeexUiProcessPropertiesSnapshot>((snapshot, fieldId) => {
    if (Object.prototype.hasOwnProperty.call(raw, fieldId)) {
      snapshot[fieldId] = getTextValue(raw[fieldId]);
    }
    return snapshot;
  }, {});
};

export function buildPipeexUiWizardSnapshot(
  source: PipeexUiWizardProfileSource | null | undefined
): PipeexUiWizardSnapshot | undefined {
  if (!source) {
    return undefined;
  }

  const additionalCategoriesParameterDestination = normalizeParameterDestination(
    source.additionalCategories.parameterDestination
  );
  const progress = normalizePipeexUiWizardProgressSnapshot(source.progress);

  const sharedParameterFileContent =
    source.parameterWorkflow.useDefaultFile === true
      ? undefined
      : typeof source.parameterWorkflow.sharedParameterFileContent === 'string' &&
          source.parameterWorkflow.sharedParameterFileContent.trim().length > 0
        ? source.parameterWorkflow.sharedParameterFileContent
        : undefined;

  return {
    setup: {
      workflowMode: normalizeWorkflowMode(source.parameterWorkflow.workflowMode),
      useDefaultFile: source.parameterWorkflow.useDefaultFile === true,
      sharedParameterFilePath: getTextValue(source.parameterWorkflow.sharedParameterFilePath),
      ...(sharedParameterFileContent ? { sharedParameterFileContent } : {}),
      parameters: normalizeParameterOptionsSnapshot(source.parameterWorkflow.parameters),
      selectedCategories: normalizeAllowedStringArray(
        source.parameterWorkflow.selectedCategories,
        PARAMETER_WORKFLOW_CATEGORY_OPTIONS,
        DEFAULT_PARAMETER_WORKFLOW_SELECTED_CATEGORIES
      ),
      systemGroupingPreference:
        getTextValue(source.parameterWorkflow.systemGroupingPreference).trim() ||
        DEFAULT_PARAMETER_WORKFLOW_SYSTEM_GROUPING,
    },
    menu: {
      selectedOption: normalizeWorkflowOption(source.menu.selectedOption),
      objectTypeValue: getTextValue(source.menu.objectTypeValue),
      sKeyValue: getTextValue(source.menu.sKeyValue),
      pipingSpecValue: getTextValue(source.menu.pipingSpecValue),
      systemGroupingValue:
        getTextValue(source.menu.systemGroupingValue).trim() ||
        getTextValue(source.parameterWorkflow.systemGroupingPreference).trim() ||
        DEFAULT_PARAMETER_WORKFLOW_SYSTEM_GROUPING,
    },
    connector: {
      parameterDestination: normalizeParameterDestination(
        source.connector.parameterDestination
      ),
      parameterBindingKind: normalizeParameterBindingKind(
        source.connector.parameterBindingKind
      ),
      fields: buildFormFieldSnapshot(
        PIPEEX_UI_CONNECTOR_FIELD_IDS,
        source.connector
      ),
    },
    bolt: {
      parameterDestination: normalizeParameterDestination(source.bolt.parameterDestination),
      parameterBindingKind: normalizeParameterBindingKind(source.bolt.parameterBindingKind),
      projectParameterCategories: normalizeAllowedStringArray(
        source.bolt.projectParameterCategories,
        BOLT_PROJECT_PARAMETER_CATEGORY_OPTIONS,
        ['Pipe Accessories']
      ),
      fields: buildFormFieldSnapshot(PIPEEX_UI_BOLT_FIELD_IDS, source.bolt),
    },
    additionalCategories: {
      parameterDestination: additionalCategoriesParameterDestination,
      parameterBindingKind: normalizeAdditionalCategoriesParameterBindingKind(
        source.additionalCategories.parameterBindingKind
      ),
      projectParameterCategories: normalizeAllowedStringArray(
        source.additionalCategories.projectParameterCategories,
        ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS,
        [...ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS]
      ),
      fields: buildFormFieldSnapshot(
        PIPEEX_UI_ADDITIONAL_CATEGORIES_FIELD_IDS,
        source.additionalCategories
      ),
    },
    processProperties: {
      processConditionsChecked: source.process.processConditionsChecked === true,
      projectPropertiesChecked: source.process.projectPropertiesChecked === true,
      includeDefaultPipeexParameters: normalizeOptionalBoolean(
        source.process.includeDefaultPipeexParameters
      ),
      fields: normalizeProcessPropertiesSnapshot(
        buildFormFieldSnapshot(PIPEEX_UI_PROCESS_FIELD_IDS, source.process)
      ),
    },
    mapping: buildPipeexUiWizardMappingSnapshot(source.mapping),
    ...(progress ? { progress } : {}),
  };
}

export function normalizePipeexUiWizardSnapshot(
  raw: unknown
): PipeexUiWizardSnapshot | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  const snapshot = raw as {
    setup?: unknown;
    menu?: unknown;
    connector?: unknown;
    bolt?: unknown;
    additionalCategories?: unknown;
    processProperties?: unknown;
    mapping?: unknown;
    progress?: unknown;
  };
  const setup = snapshot.setup && typeof snapshot.setup === 'object'
    ? snapshot.setup as Record<string, unknown>
    : {};
  const menu = snapshot.menu && typeof snapshot.menu === 'object'
    ? snapshot.menu as Record<string, unknown>
    : {};
  const connector = snapshot.connector && typeof snapshot.connector === 'object'
    ? snapshot.connector as Record<string, unknown>
    : {};
  const bolt = snapshot.bolt && typeof snapshot.bolt === 'object'
    ? snapshot.bolt as Record<string, unknown>
    : {};
  const additionalCategories =
    snapshot.additionalCategories && typeof snapshot.additionalCategories === 'object'
      ? snapshot.additionalCategories as Record<string, unknown>
      : {};
  const processProperties =
    snapshot.processProperties && typeof snapshot.processProperties === 'object'
      ? snapshot.processProperties as Record<string, unknown>
      : {};
  const mapping = snapshot.mapping && typeof snapshot.mapping === 'object'
    ? snapshot.mapping as Record<string, unknown>
    : {};
  const additionalCategoriesParameterDestination = normalizeParameterDestination(
    additionalCategories.parameterDestination
  );
  const additionalCategoriesFields = normalizeFormFieldSnapshot(
    (additionalCategories as { fields?: unknown }).fields,
    PIPEEX_UI_ADDITIONAL_CATEGORIES_FIELD_IDS
  );
  const progress = normalizePipeexUiWizardProgressSnapshot(snapshot.progress);

  const normalizedSharedParameterFileContent =
    normalizeBoolean(setup.useDefaultFile, true)
      ? undefined
      : typeof setup.sharedParameterFileContent === 'string' &&
          setup.sharedParameterFileContent.trim().length > 0
        ? setup.sharedParameterFileContent
        : undefined;

  return {
    setup: {
      workflowMode: normalizeWorkflowMode(setup.workflowMode),
      useDefaultFile: normalizeBoolean(setup.useDefaultFile, true),
      sharedParameterFilePath:
        getTextValue(setup.sharedParameterFilePath).trim() ||
        DEFAULT_PARAMETER_WORKFLOW_SHARED_PARAMETER_FILE_PATH,
      ...(normalizedSharedParameterFileContent
        ? { sharedParameterFileContent: normalizedSharedParameterFileContent }
        : {}),
      parameters: normalizeParameterOptionsSnapshot(setup.parameters),
      selectedCategories: normalizeAllowedStringArray(
        setup.selectedCategories,
        PARAMETER_WORKFLOW_CATEGORY_OPTIONS,
        DEFAULT_PARAMETER_WORKFLOW_SELECTED_CATEGORIES
      ),
      systemGroupingPreference:
        getTextValue(setup.systemGroupingPreference).trim() ||
        DEFAULT_PARAMETER_WORKFLOW_SYSTEM_GROUPING,
    },
    menu: {
      selectedOption: normalizeWorkflowOption(menu.selectedOption),
      objectTypeValue: getTextValue(menu.objectTypeValue),
      sKeyValue: getTextValue(menu.sKeyValue),
      pipingSpecValue: getTextValue(menu.pipingSpecValue),
      systemGroupingValue:
        getTextValue(menu.systemGroupingValue).trim() ||
        getTextValue(setup.systemGroupingPreference).trim() ||
        DEFAULT_PARAMETER_WORKFLOW_SYSTEM_GROUPING,
    },
    connector: {
      parameterDestination: normalizeParameterDestination(connector.parameterDestination),
      parameterBindingKind: normalizeParameterBindingKind(connector.parameterBindingKind),
      fields: normalizeFormFieldSnapshot(
        (connector as { fields?: unknown }).fields,
        PIPEEX_UI_CONNECTOR_FIELD_IDS
      ),
    },
    bolt: {
      parameterDestination: normalizeParameterDestination(bolt.parameterDestination),
      parameterBindingKind: normalizeParameterBindingKind(bolt.parameterBindingKind),
      projectParameterCategories: normalizeAllowedStringArray(
        bolt.projectParameterCategories,
        BOLT_PROJECT_PARAMETER_CATEGORY_OPTIONS,
        ['Pipe Accessories']
      ),
      fields: normalizeFormFieldSnapshot(
        (bolt as { fields?: unknown }).fields,
        PIPEEX_UI_BOLT_FIELD_IDS
      ),
    },
    additionalCategories: {
      parameterDestination: additionalCategoriesParameterDestination,
      parameterBindingKind: normalizeAdditionalCategoriesParameterBindingKind(
        additionalCategories.parameterBindingKind
      ),
      projectParameterCategories: normalizeAllowedStringArray(
        additionalCategories.projectParameterCategories,
        ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS,
        [...ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS]
      ),
      fields: additionalCategoriesFields,
    },
    processProperties: {
      processConditionsChecked: normalizeBoolean(
        processProperties.processConditionsChecked
      ),
      projectPropertiesChecked: normalizeBoolean(
        processProperties.projectPropertiesChecked
      ),
      includeDefaultPipeexParameters: normalizeOptionalBoolean(
        processProperties.includeDefaultPipeexParameters
      ),
      fields: normalizeProcessPropertiesSnapshot(
        (processProperties as { fields?: Partial<Record<string, unknown>> }).fields
      ),
    },
    mapping: normalizePipeexUiWizardMappingSnapshot(mapping),
    ...(progress ? { progress } : {}),
  };
}
